/*
  The Enricher - Lottie feature icons.

  Problem this solves: the page loaded @dotlottie/player from unpkg as an ES
  module, which then pulled several more chunks plus a Lottie renderer, then each
  player fetched its own JSON (136KB across four icons). That is ~390KB and 10
  requests over a cross-origin waterfall for four 64px decorative icons, with all
  four autoplaying on a loop. On mobile the chain often had not finished before
  the section scrolled past, and once running, four looping players got throttled.

  Now:
  - Mobile, touch devices and reduced-motion get an inline static SVG of the exact
    same artwork (generated from the final frame of each source file). No player,
    no JSON, no third-party request.
  - Pointer devices load the player lazily, only when the icons approach the
    viewport, and each animation plays on hover the way the artwork intends
    rather than looping forever.
*/
(function () {
  var PLAYER_SRC = 'https://unpkg.com/@dotlottie/player-component@2.7.12/dist/dotlottie-player.mjs';

  // Maps a source filename fragment to a key in ENR_LOTTIE_ICONS.
  var ICON_MATCHES = [
    ['brain-mental', 'brain'],
    ['badge-ribbon', 'badge'],
    ['hanging-sign-pet-print', 'paw'],
    ['delivery', 'delivery']
  ];

  function iconKeyFor(src) {
    for (var i = 0; i < ICON_MATCHES.length; i++) {
      if (src.indexOf(ICON_MATCHES[i][0]) !== -1) return ICON_MATCHES[i][1];
    }
    return null;
  }

  function players() {
    return Array.prototype.slice.call(document.querySelectorAll('dotlottie-player:not([data-enr-done])'));
  }

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function shouldAnimate() {
    // Animated on every device now, by request. Only reduced-motion opts out.
    return !prefersReducedMotion();
  }

  function renderStatic(el) {
    var icons = window.ENR_LOTTIE_ICONS;
    var key = iconKeyFor(el.getAttribute('src') || '');
    if (!icons || !key || !icons[key]) return false;

    var icon = icons[key];
    var svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', icon.viewBox);
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
    svg.setAttribute('class', 'enr-lottie-static');
    svg.innerHTML = icon.inner;

    // Preserve the sizing the block asked for.
    var w = el.style.width || '64px';
    var h = el.style.height || '64px';
    svg.style.width = w;
    svg.style.height = h;
    svg.style.display = 'block';

    el.setAttribute('data-enr-done', 'static');
    el.parentNode.replaceChild(svg, el);
    return true;
  }

  function loadStaticIcons(done) {
    if (window.ENR_LOTTIE_ICONS) return done();
    var s = document.createElement('script');
    s.src = window.__ENR_LOTTIE_ICONS_URL || '';
    if (!s.src) return done();
    s.onload = done;
    s.onerror = done;
    document.head.appendChild(s);
  }

  var playerRequested = false;
  function loadPlayer() {
    if (playerRequested) return;
    playerRequested = true;
    var s = document.createElement('script');
    s.type = 'module';
    s.src = PLAYER_SRC;
    document.head.appendChild(s);
  }

  function setupAnimated(list) {
    // Only fetch the player when the icons are actually close to being seen.
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        loadPlayer();
        io.unobserve(entry.target);
      });
    }, { rootMargin: '300px' });

    list.forEach(function (el) {
      el.setAttribute('data-enr-done', 'animated');
      // Loop continuously rather than waiting for a hover that never comes on touch.
      el.setAttribute('loop', '');
      el.setAttribute('autoplay', '');

      /*
        Before the player upgrades it, dotlottie-player is an unknown element and
        so defaults to display:inline, which ignores its inline width/height and
        measures 0x0. That both breaks the observer and causes a layout shift
        when the art appears, so give it a box up front.
      */
      el.style.display = 'block';
      if (!el.style.width) el.style.width = '64px';
      if (!el.style.height) el.style.height = '64px';

      // Observe the parent as well: it always has layout, even if the element
      // somehow still measures zero.
      io.observe(el.parentElement || el);

    });

    /*
      Safety net: if the observer never fires (zero-height container, an odd
      layout, IO unsupported) load the player once the browser is idle so the
      icons can never end up permanently blank.
    */
    var idle = window.requestIdleCallback || function (cb) { return setTimeout(cb, 2500); };
    idle(function () { loadPlayer(); });
  }

  function init() {
    var list = players();
    if (!list.length) return;

    if (shouldAnimate()) {
      setupAnimated(list);
    } else {
      loadStaticIcons(function () {
        list.forEach(function (el) {
          if (!renderStatic(el) && el.parentNode) {
            // No matching artwork: leave the element but stop it looping.
            el.removeAttribute('autoplay');
            el.removeAttribute('loop');
          }
        });
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
